// eslint-disable-next-line no-restricted-imports
import { Button, createStyles } from '@mantine/core';
import { Button as ButtonFoundation } from '@repo/foundations';
import dayjs from 'dayjs';
import { forwardRef } from 'react';

export interface DateRange {
	from: Date | null;
	to: Date | null;
}

export interface DateRangeOption {
	id: string;
	label: string;
	timeRange?: string;
	range: DateRange | (() => DateRange);
}

export interface TimeRangeValue {
	timeRange: string;
	dateRange: DateRange;
}

export interface DateRangeButtonGroupProps {
	/** Currently selected option ID */
	selectedOptionId: string;
	/** Callback when date range changes */
	onChange: (range: DateRange, optionId: string) => void;
	/** Button variant */
	variant?: 'default' | 'primary' | 'tertiary';
	/** Whether the component is disabled */
	disabled?: boolean;
	/** Custom options to override default day, week, month */
	options?: DateRangeOption[];
	/** ID for component */
	id?: string;
}

const useStyles = createStyles((theme) => ({
	buttonGroup: {
		display: 'inline-flex',
		'& > button': {
			borderRadius: 0,
		},
		'& > button:first-of-type': {
			borderTopLeftRadius: theme.other.space[2],
			borderBottomLeftRadius: theme.other.space[2],
		},
		'& > button:last-of-type': {
			borderTopRightRadius: theme.other.space[2],
			borderBottomRightRadius: theme.other.space[2],
		},
	},
}));

const DateRangeButtonGroup = forwardRef<
	HTMLButtonElement,
	DateRangeButtonGroupProps
>(
	(
		{ selectedOptionId, onChange, variant = 'default', disabled, options, id },
		ref
	) => {
		const { classes } = useStyles();

		const defaultOptions: DateRangeOption[] = [
			{
				id: 'day',
				label: 'Day',
				timeRange: '24h',
				range: () => {
					const today = new Date();
					return {
						from: today,
						to: today,
					};
				},
			},
			{
				id: 'week',
				label: 'Week',
				timeRange: '7d',
				range: () => {
					const today = new Date();
					const startOfWeek = dayjs(today).startOf('week').toDate();
					const endOfWeek = dayjs(today).endOf('week').toDate();
					return {
						from: startOfWeek,
						to: endOfWeek,
					};
				},
			},
			{
				id: 'month',
				label: 'Month',
				timeRange: '30d',
				range: () => {
					const today = new Date();
					const startOfMonth = dayjs(today).startOf('month').toDate();
					const endOfMonth = dayjs(today).endOf('month').toDate();
					return {
						from: startOfMonth,
						to: endOfMonth,
					};
				},
			},
		];

		const effectiveOptions = options || defaultOptions;

		const handleOptionSelect = (optionId: string) => {
			if (optionId === selectedOptionId) return;

			const option = effectiveOptions.find((opt) => opt.id === optionId);
			if (!option) return;

			const range =
				typeof option.range === 'function' ? option.range() : option.range;
			onChange(range, optionId);
		};

		return (
			<Button.Group id={id} className={classes.buttonGroup}>
				{effectiveOptions.map((option) => (
					<ButtonFoundation
						key={option.id}
						variant={variant}
						onClick={() => handleOptionSelect(option.id)}
						highlight={selectedOptionId === option.id}
						disabled={disabled}
						data-testid={`date-range-${option.id}`}
						ref={option.id === effectiveOptions[0].id ? ref : undefined}
					>
						{option.label}
					</ButtonFoundation>
				))}
			</Button.Group>
		);
	}
);

DateRangeButtonGroup.displayName = 'DateRangeButtonGroup';

export default DateRangeButtonGroup;
